/**
 * Low-level byte helpers built only on Uint8Array / DataView / BigInt.
 * No Node.js Buffer and no polyfills, so this runs natively in browsers
 * and in modern runtimes alike.
 *
 * Every multi-byte integer is big-endian, matching Qt's QDataStream format.
 *
 * @module qtdatastream-web/bytes
 */
/** Concatenate several byte arrays into a single Uint8Array. */
export function concat(chunks) {
    let total = 0;
    for (const c of chunks)
        total += c.length;
    const out = new Uint8Array(total);
    let offset = 0;
    for (const c of chunks) {
        out.set(c, offset);
        offset += c.length;
    }
    return out;
}
/** Coerce any byte-ish input into a Uint8Array (without copying when possible). */
export function toUint8(input) {
    if (input instanceof Uint8Array)
        return input;
    if (input instanceof ArrayBuffer)
        return new Uint8Array(input);
    if (ArrayBuffer.isView(input)) {
        return new Uint8Array(input.buffer, input.byteOffset, input.byteLength);
    }
    if (Array.isArray(input))
        return Uint8Array.from(input);
    throw new TypeError('Expected Uint8Array, ArrayBuffer, TypedArray, or number[]');
}
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder('utf-8');
/** Encode a string as UTF-8 bytes. */
export function encodeUtf8(str) {
    return textEncoder.encode(str);
}
/** Decode UTF-8 bytes into a string. */
export function decodeUtf8(bytes) {
    return textDecoder.decode(bytes);
}
/**
 * Encode a JS string as big-endian UTF-16 (Qt QString wire format).
 * JS strings are already UTF-16, so each code unit is emitted big-endian,
 * which naturally preserves surrogate pairs for astral characters.
 */
export function encodeUtf16BE(str) {
    const out = new Uint8Array(str.length * 2);
    const view = new DataView(out.buffer);
    for (let i = 0; i < str.length; i++) {
        view.setUint16(i * 2, str.charCodeAt(i), false);
    }
    return out;
}
/** Decode big-endian UTF-16 bytes into a string. */
export function decodeUtf16BE(bytes) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const units = [];
    for (let i = 0; i + 1 < bytes.byteLength; i += 2) {
        units.push(view.getUint16(i, false));
    }
    // Build the string in chunks to avoid call-stack limits on huge inputs.
    let str = '';
    const CHUNK = 8192;
    for (let i = 0; i < units.length; i += CHUNK) {
        str += String.fromCharCode(...units.slice(i, i + CHUNK));
    }
    return str;
}
export function fromInt8(v) {
    const b = new Uint8Array(1);
    new DataView(b.buffer).setInt8(0, v);
    return b;
}
export function fromInt16BE(v) {
    const b = new Uint8Array(2);
    new DataView(b.buffer).setInt16(0, v, false);
    return b;
}
export function fromUint16BE(v) {
    const b = new Uint8Array(2);
    new DataView(b.buffer).setUint16(0, v & 0xffff, false);
    return b;
}
export function fromInt32BE(v) {
    const b = new Uint8Array(4);
    new DataView(b.buffer).setInt32(0, v, false);
    return b;
}
export function fromUint32BE(v) {
    const b = new Uint8Array(4);
    new DataView(b.buffer).setUint32(0, v >>> 0, false);
    return b;
}
export function fromBigInt64BE(v) {
    const b = new Uint8Array(8);
    new DataView(b.buffer).setBigInt64(0, BigInt.asIntN(64, BigInt(v)), false);
    return b;
}
export function fromBigUint64BE(v) {
    const b = new Uint8Array(8);
    new DataView(b.buffer).setBigUint64(0, BigInt.asUintN(64, BigInt(v)), false);
    return b;
}
export function fromDoubleBE(v) {
    const b = new Uint8Array(8);
    new DataView(b.buffer).setFloat64(0, v, false);
    return b;
}
//# sourceMappingURL=bytes.js.map